import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ReturnFormPage = () => {
  const { API, language } = useApp();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    orderNumber: '',
    accountHolder: '',
    iban: '',
    returnReason: '',
    details: '',
  });

  const translations = {
    ro: {
      title: 'Formular de Retur',
      subtitle: 'Completează formularul de mai jos pentru a solicita returnarea produselor.',
      fullName: 'Nume și prenume',
      email: 'Email',
      phone: 'Telefon',
      orderNumber: 'Număr comandă',
      accountHolder: 'Titular cont',
      iban: 'Cont IBAN',
      returnReason: 'Motivul returnării',
      details: 'Detalii',
      detailsPlaceholder: 'Descrie motivul returnării și orice alte detalii relevante...',
      acceptPolicy: 'Am citit și sunt de acord cu',
      returnPolicy: 'Politica de Retur',
      submit: 'Trimite cererea',
      reasons: {
        not_liked: 'Nu mi-a plăcut',
        damaged: 'Produsul a ajuns deteriorat',
        delayed: 'Produsul a fost livrat cu întârziere',
        defect: 'Produs defect',
        wrong_product: 'Nu am primit produsul pe care l-am comandat',
        other: 'Alt motiv, furnizează detalii',
      },
      successTitle: 'Cerere trimisă cu succes!',
      successMessage: 'Am primit cererea ta de retur. Te vom contacta în curând pentru a confirma procesul de returnare.',
      backToShop: 'Înapoi la magazin',
      requiredField: 'Acest câmp este obligatoriu',
    },
    en: {
      title: 'Return Form',
      subtitle: 'Fill out the form below to request a product return.',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      orderNumber: 'Order Number',
      accountHolder: 'Account Holder',
      iban: 'IBAN Account',
      returnReason: 'Return Reason',
      details: 'Details',
      detailsPlaceholder: 'Describe the reason for return and any other relevant details...',
      acceptPolicy: 'I have read and agree to the',
      returnPolicy: 'Return Policy',
      submit: 'Submit Request',
      reasons: {
        not_liked: "I didn't like it",
        damaged: 'Product arrived damaged',
        delayed: 'Product was delivered late',
        defect: 'Defective product',
        wrong_product: "I didn't receive the product I ordered",
        other: 'Other reason, provide details',
      },
      successTitle: 'Request Submitted Successfully!',
      successMessage: 'We have received your return request. We will contact you soon to confirm the return process.',
      backToShop: 'Back to Shop',
      requiredField: 'This field is required',
    },
  };

  const t = translations[language] || translations.ro;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedPolicy) {
      toast.error(language === 'ro' ? 'Te rugăm să accepți politica de retur' : 'Please accept the return policy');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/returns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          language,
          submitted_at: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit return request');
      }

      setSubmitted(true);
      toast.success(language === 'ro' ? 'Cererea a fost trimisă!' : 'Request submitted!');
    } catch (err) {
      console.error('Return form error:', err);
      toast.error(language === 'ro' ? 'A apărut o eroare. Te rugăm să încerci din nou.' : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-4">{t.successTitle}</h1>
            <p className="text-muted-foreground mb-8">{t.successMessage}</p>
            <Button asChild className="bg-primary">
              <Link to="/shop">{t.backToShop}</Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-2">{t.title}</h1>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white border border-gray-100 rounded-xl p-8">
          {/* Full Name */}
          <div>
            <Label htmlFor="fullName">{t.fullName} *</Label>
            <Input
              id="fullName"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleInputChange}
              className="mt-1"
            />
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email">{t.email} *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="mt-1"
            />
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone">{t.phone} *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              required
              placeholder="07xxxxxxxx"
              value={formData.phone}
              onChange={handleInputChange}
              className="mt-1"
            />
          </div>

          {/* Order Number */}
          <div>
            <Label htmlFor="orderNumber">{t.orderNumber} *</Label>
            <Input
              id="orderNumber"
              name="orderNumber"
              required
              placeholder="Ex: ORD-12345"
              value={formData.orderNumber}
              onChange={handleInputChange}
              className="mt-1"
            />
          </div>

          <div className="border-t pt-6">
            <h3 className="font-semibold text-[#1A1625] mb-4">{language === 'ro' ? 'Date bancare pentru rambursare' : 'Bank details for refund'}</h3>
            
            {/* Account Holder */}
            <div className="mb-4">
              <Label htmlFor="accountHolder">{t.accountHolder} *</Label>
              <Input
                id="accountHolder"
                name="accountHolder"
                required
                value={formData.accountHolder}
                onChange={handleInputChange}
                className="mt-1"
              />
            </div>

            {/* IBAN */}
            <div>
              <Label htmlFor="iban">{t.iban} *</Label>
              <Input
                id="iban"
                name="iban"
                required
                placeholder="RO49AAAA1B31007593840000"
                value={formData.iban}
                onChange={handleInputChange}
                className="mt-1 font-mono"
              />
            </div>
          </div>

          {/* Return Reason */}
          <div>
            <Label htmlFor="returnReason">{t.returnReason} *</Label>
            <Select value={formData.returnReason} onValueChange={(val) => setFormData((prev) => ({ ...prev, returnReason: val }))}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={language === 'ro' ? 'Selectează motivul' : 'Select reason'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_liked">{t.reasons.not_liked}</SelectItem>
                <SelectItem value="damaged">{t.reasons.damaged}</SelectItem>
                <SelectItem value="delayed">{t.reasons.delayed}</SelectItem>
                <SelectItem value="defect">{t.reasons.defect}</SelectItem>
                <SelectItem value="wrong_product">{t.reasons.wrong_product}</SelectItem>
                <SelectItem value="other">{t.reasons.other}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Details */}
          <div>
            <Label htmlFor="details">{t.details}</Label>
            <Textarea
              id="details"
              name="details"
              value={formData.details}
              onChange={handleInputChange}
              placeholder={t.detailsPlaceholder}
              className="mt-1"
              rows={4}
            />
          </div>

          {/* Accept Policy */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="acceptPolicy"
              checked={acceptedPolicy}
              onCheckedChange={setAcceptedPolicy}
              className="mt-1"
            />
            <Label htmlFor="acceptPolicy" className="cursor-pointer text-sm">
              {t.acceptPolicy}{' '}
              <Link to="/page/politica-de-retur" className="text-primary hover:underline">
                {t.returnPolicy}
              </Link>
              {' *'}
            </Label>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading || !acceptedPolicy}
            className="w-full h-12 bg-primary hover:bg-purple-600 text-white rounded-full disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              t.submit
            )}
          </Button>
        </form>
      </div>
    </Layout>
  );
};

export default ReturnFormPage;
